import * as yup from "yup";

import { MemberVerification, TenantStatus } from "../tenant/types";

// Subdomains the platform itself uses, so no tenant can take them.
export const RESERVED_TENANT_SLUGS = ["app", "admin", "api", "www", "staging", "mail", "static", "cdn"];

export const TENANT_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;
export const HOSTNAME_PATTERN = /^(?=.{1,255}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;
export const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;
const EMAIL_PATTERN = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;
// "Name <address>" or a bare address, as SES accepts in Source.
const SENDER_PATTERN = /^(?:[^<>]+<[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+>|[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+)$/;

export const invalidTenantSlug =
  "Slug must be 1-40 lowercase letters, digits or hyphens, not starting or ending with a hyphen.";
export const reservedTenantSlug = "This slug is reserved for the platform.";
export const invalidTenantDomain = "Domain must be a hostname like members.example.org.";
export const invalidHexColor = "Colour must be a hex value like #7F3DFF.";
export const invalidSender = 'Sender must be an email address or "Name <address>".';

// Empty strings count as "not set" so cleared form fields pass.
const optional = (pattern: RegExp, message: string) =>
  yup.string().trim().nullable().matches(pattern, { message, excludeEmptyString: true });

// null clears a branding key on update; omitted keys are left alone.
export const tenantBrandingSchema = yup
  .object({
    appName: yup.string().trim().nullable().max(60),
    shortName: yup.string().trim().nullable().max(30),
    logoUrl: yup.string().trim().nullable().url(),
    iconUrl: yup.string().trim().nullable().url(),
    primaryColor: optional(HEX_COLOR_PATTERN, invalidHexColor),
    secondaryColor: optional(HEX_COLOR_PATTERN, invalidHexColor),
    supportEmail: optional(EMAIL_PATTERN, "Support email must be an email address."),
    websiteUrl: yup.string().trim().nullable().url(),
  })
  .nullable()
  .default(undefined);

const tenantFields = {
  name: yup.string().trim().max(100, "Name must be at most 100 characters."),
  domain: yup
    .string()
    .trim()
    .lowercase()
    .nullable()
    .matches(HOSTNAME_PATTERN, { message: invalidTenantDomain, excludeEmptyString: true }),
  branding: tenantBrandingSchema,
  sesFromAddress: optional(SENDER_PATTERN, invalidSender),
  sesReplyTo: optional(EMAIL_PATTERN, "Reply-to must be an email address."),
  memberVerification: yup
    .mixed<MemberVerification>()
    .oneOf(Object.values(MemberVerification))
    .nullable(),
  // Community a verified NIN joins (and the identity it creates); null for none.
  ninCommunityAlias: yup.string().trim().nullable().max(50, "Community alias must be at most 50 characters."),
  walletEnabled: yup.boolean().nullable(),
  pointsEnabled: yup.boolean().nullable(),
  referralsEnabled: yup.boolean().nullable(),
  // Points-to-cash: naira per point (null turns spending off), the largest
  // share of an offer's price points may cover, and the smallest cash-out.
  pointValue: yup.number().nullable().moreThan(0, "Point value must be above zero.").max(100000, "Point value is too high."),
  pointsMaxSharePercent: yup.number().nullable().integer().min(0, "Share must be 0 to 100.").max(100, "Share must be 0 to 100."),
  pointsMinCashout: yup.number().nullable().integer().min(0, "Minimum cash-out can't be negative."),
};

export const createTenantSchema = yup.object({
  ...tenantFields,
  name: tenantFields.name.required("Name is required."),
  slug: yup
    .string()
    .trim()
    .lowercase()
    .required("Slug is required.")
    .matches(TENANT_SLUG_PATTERN, invalidTenantSlug)
    .notOneOf(RESERVED_TENANT_SLUGS, reservedTenantSlug),
});

// The slug can't change after creation: session cookies and subdomains use it.
export const updateTenantSchema = yup.object({
  ...tenantFields,
  name: tenantFields.name.nullable().min(1, "Name can't be empty."),
  status: yup.mixed<TenantStatus>().oneOf(Object.values(TenantStatus)).nullable(),
});
